"use client";

import { c as _c } from "react/compiler-runtime";
import { Plus } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { CustomTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
type Direction = "up" | "down" | "left" | "right";
interface SpeedDialAction {
  icon: React.ReactNode;
  label: string;
  key: string;
  action: () => void;
}
interface SpeedDialProps extends React.HTMLAttributes<HTMLDivElement> {
  direction?: Direction;
  actionButtons: SpeedDialAction[];
  /** Accessible name for the main toggle when closed. */
  triggerLabel?: string;
  /** Accessible name for the main toggle when open. */
  closeLabel?: string;
}
const shellClass = "flex size-11 shrink-0 cursor-pointer touch-manipulation items-center justify-center rounded-xl border border-border/80 bg-background/85 text-foreground shadow-lg backdrop-blur-xl transition-colors hover:bg-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.97]";
const menuAnchorClass: Record<Direction, string> = {
  up: "bottom-full left-1/2 mb-2 -translate-x-1/2 origin-bottom",
  down: "top-full left-1/2 mt-2 -translate-x-1/2 origin-top",
  left: "right-full top-1/2 mr-2 -translate-y-1/2 origin-right",
  right: "left-full top-1/2 ml-2 -translate-y-1/2 origin-left"
};
const menuLayoutClass: Record<Direction, string> = {
  up: "flex flex-col-reverse items-center gap-2.5",
  down: "flex flex-col items-center gap-2.5",
  left: "flex flex-row-reverse items-center gap-2.5",
  right: "flex flex-row items-center gap-2.5"
};
function arrowKeysForDirection(direction: Direction) {
  if (direction === "up") return {
    prev: "ArrowDown",
    next: "ArrowUp"
  };
  if (direction === "down") return {
    prev: "ArrowUp",
    next: "ArrowDown"
  };
  if (direction === "left") return {
    prev: "ArrowRight",
    next: "ArrowLeft"
  };
  return {
    prev: "ArrowLeft",
    next: "ArrowRight"
  };
}
export default function SpeedDial(t0) {
  const $ = _c(50);
  let actionButtons;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      direction: t1,
      actionButtons,
      triggerLabel: t2,
      closeLabel: t3,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = actionButtons;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    actionButtons = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const direction = t1 === undefined ? "right" : t1;
  const triggerLabel = t2 === undefined ? "Open actions menu" : t2;
  const closeLabel = t3 === undefined ? "Close actions menu" : t3;
  const menuId = useId();
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const itemRefs = useRef(t4);
  const [open, setOpen] = useState(false);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setOpen(false);
      triggerRef.current?.focus();
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const close = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setOpen(_temp);
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const toggle = t6;
  let t7;
  let t8;
  if ($[10] !== open) {
    t7 = () => {
      if (!open) {
        return;
      }
      const onPointerDown = event => {
        if (rootRef.current?.contains(event.target as Node)) {
          return;
        }
        setOpen(false);
      };
      document.addEventListener("pointerdown", onPointerDown);
      return () => document.removeEventListener("pointerdown", onPointerDown);
    };
    t8 = [open];
    $[10] = open;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[13] !== open) {
    t9 = () => {
      if (!open) {
        return;
      }
      itemRefs.current[0]?.focus();
    };
    t10 = [open];
    $[13] = open;
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useEffect(t9, t10);
  let t11;
  if ($[16] !== actionButtons.length) {
    t11 = index => {
      const count = actionButtons.length;
      if (count === 0) {
        return;
      }
      const next = (index + count) % count;
      itemRefs.current[next]?.focus();
    };
    $[16] = actionButtons.length;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const focusItem = t11;
  let t12;
  if ($[18] !== actionButtons.length || $[19] !== direction || $[20] !== focusItem) {
    t12 = event_0 => {
      const {
        prev,
        next: next_0
      } = arrowKeysForDirection(direction);
      const activeIndex = itemRefs.current.findIndex(_temp2);
      bb67: switch (event_0.key) {
        case "Escape":
          {
            event_0.preventDefault();
            close();
            break bb67;
          }
        case "Tab":
          {
            setOpen(false);
            break bb67;
          }
        case "Home":
          {
            event_0.preventDefault();
            itemRefs.current[0]?.focus();
            break bb67;
          }
        case "End":
          {
            event_0.preventDefault();
            itemRefs.current[actionButtons.length - 1]?.focus();
            break bb67;
          }
        default:
          {
            if (event_0.key === prev) {
              event_0.preventDefault();
              focusItem(activeIndex <= 0 ? actionButtons.length - 1 : activeIndex - 1);
            } else {
              if (event_0.key === next_0) {
                event_0.preventDefault();
                focusItem(activeIndex < 0 ? 0 : activeIndex + 1);
              }
            }
          }
      }
    };
    $[18] = actionButtons.length;
    $[19] = direction;
    $[20] = focusItem;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const onMenuKeyDown = t12;
  let t13;
  if ($[22] !== className) {
    t13 = cn("relative inline-flex cursor-pointer", className);
    $[22] = className;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = open ? closeLabel : triggerLabel;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn(shellClass, "relative z-20");
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = open ? closeLabel : triggerLabel;
  const t17 = open && "rotate-45";
  let t18;
  if ($[25] !== t17) {
    t18 = cn("speed-dial-trigger-icon flex items-center justify-center transition-transform duration-150 ease-out pointer-events-none", t17);
    $[25] = t17;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Plus className="size-5" strokeWidth={2.25} />;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== t18) {
    t20 = <span aria-hidden="true" className={t18}>{t19}</span>;
    $[28] = t18;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== menuId || $[31] !== open || $[32] !== t16 || $[33] !== t20) {
    t21 = <button ref={triggerRef} type="button" className={t15} aria-expanded={open} aria-haspopup="menu" aria-controls={menuId} aria-label={t16} onClick={toggle}>{t20}</button>;
    $[30] = menuId;
    $[31] = open;
    $[32] = t16;
    $[33] = t20;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== t14 || $[36] !== t21) {
    t22 = <CustomTooltip content={t14} side="left">{t21}</CustomTooltip>;
    $[35] = t14;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== actionButtons || $[39] !== direction || $[40] !== menuId || $[41] !== onMenuKeyDown || $[42] !== open) {
    t23 = open ? <ul id={menuId} role="menu" aria-orientation={direction === "up" || direction === "down" ? "vertical" : "horizontal"} onKeyDown={onMenuKeyDown} className={cn("absolute z-10 m-0 list-none p-0", menuAnchorClass[direction], menuLayoutClass[direction])}>{actionButtons.map((action, index_0) => <li key={action.key} role="none" className="speed-dial-item list-none" style={{
        "--i": index_0
      } as React.CSSProperties}><CustomTooltip content={action.label} side="left"><button ref={node_0 => {
            itemRefs.current[index_0] = node_0;
          }} type="button" role="menuitem" className={shellClass} aria-label={action.label} onClick={() => {
            action.action();
            close();
          }}><span className="sr-only">{action.label}</span><span aria-hidden="true" className="flex size-5 items-center justify-center [&>svg]:size-5 pointer-events-none">{action.icon}</span></button></CustomTooltip></li>)}</ul> : null;
    $[38] = actionButtons;
    $[39] = direction;
    $[40] = menuId;
    $[41] = onMenuKeyDown;
    $[42] = open;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== direction || $[45] !== props || $[46] !== t13 || $[47] !== t22 || $[48] !== t23) {
    t24 = <div ref={rootRef} className={t13} data-direction={direction} {...props}>{t22}{t23}</div>;
    $[44] = direction;
    $[45] = props;
    $[46] = t13;
    $[47] = t22;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  return t24;
}
function _temp2(node) {
  return node === document.activeElement;
}
function _temp(value) {
  return !value;
}